/**
 * Spec §8 A17 — pre-pay mixed-cart refund / cancellation terms on checkout payment.
 * A18 clamp runs only when platform legal floors CONTENT is passed (STOP-A18-CONTENT:
 * empty / omitted floors → passthrough; do not invent PO floor CONTENT here).
 */

import {
  presentMixedCartRefundTerms,
  type MixedCartLegalFloorsInput,
  type MixedCartLineTermInput,
} from '../presenters/mixedCartRefundTermsPresenter.js';

export interface MixedCartRefundTermsNoticeProps {
  readonly lines: readonly MixedCartLineTermInput[];
  /** Optional floors — omit or empty for STOP-A18-CONTENT passthrough. */
  readonly legalFloors?: MixedCartLegalFloorsInput | null;
}

/**
 * Spec §8 A17 — show when cart is mixed, or when prepared lines yield a non-empty summary.
 * Ordinary-only carts stay silent (no distinct refund disclosure needed pre-pay).
 * Visual language matches checkout.slaNotice (prose notice; no AlertBanner on customer).
 */
export function MixedCartRefundTermsNotice({
  lines,
  legalFloors = null,
}: MixedCartRefundTermsNoticeProps): JSX.Element | null {
  if (lines.length === 0) {
    return null;
  }
  const view = presentMixedCartRefundTerms(lines, legalFloors);
  const showPreparedTerms = view.preparedCount > 0 && view.summary.length > 0;
  if (!view.isMixed && !showPreparedTerms) {
    return null;
  }
  if (view.summary.length === 0) {
    return null;
  }

  return (
    <div
      className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface-elevated)] px-3 py-3"
      role="status"
      data-testid="mixed-cart-refund-terms"
    >
      <p className="text-sm text-[var(--color-on-surface)]">{view.summary}</p>
      {view.isMixed || view.lineNotes.length > 1 ? (
        <ul className="mt-2 list-disc space-y-1 pl-4 text-xs text-[var(--color-on-surface-muted)]">
          {view.lineNotes.map((note) => (
            <li key={note.productId}>
              <span className="font-medium text-[var(--color-on-surface)]">{note.productName}</span>
              {': '}
              {note.termLabel}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
